import React, { useCallback, useMemo, useState } from "react";
import { DataGrid, ScrollArea, Toast } from "../../../../components";
import { getRequest, optimisticOptions, putRequest, queries } from "../../../../react-query";
import { useMutation, useQuery } from "react-query";
import ReactPaginate from "react-paginate";
import { TriangleLeftIcon, TriangleRightIcon } from "@radix-ui/react-icons";
import { GlobalSearch } from "../../../../components/filters";
import { Link, useNavigate } from "react-router-dom";
import { FaEdit, FaVideo, FaUpload, FaExternalLinkAlt, FaDrawPolygon } from "react-icons/fa";
import getStorage from "../../../../services/storageService";
import { Tooltip } from "../../../../components/tooltip";

export const ListConfig = () => {
  const navigate = useNavigate();

  let size = 10;
  const [page, setPage] = useState<number>(1);

  const [input, setInput] = useState({
    filter: "ACTIVE",
    search: "",
  });

  const client_id = getStorage('client_id') || null;
  const uin_ids = getStorage('uin_ids') || null;

 

  const {
    data: configData = { data: [], pagination: { totalPages: 0, totalCount: 0, pageSize: size } },
  }: any = useQuery(
    [queries.config, input.filter, page, size, client_id, uin_ids],
    () => {
      return getRequest(
        `/config?page=${page}&pageSize=${size}&client_id=${client_id}&uin_ids=${uin_ids}`
      );
    },
    {
      enabled: !!page || !!client_id || !!uin_ids,
    }
  );

  // Destructuring Data
  const { data: config = [], pagination } = configData;

  const totalPages = useMemo(
    () => pagination?.totalPages || Math.ceil((pagination?.totalCount || 0) / (pagination?.pageSize || size)),
    [pagination]
  );

  const onPageChangeHandler = useCallback((selected: { selected: number }) => {
    setPage(selected.selected + 1);
  }, []);


 

  const columns = useMemo(
    () => [
      { Header: "UIN ID", accessor: "uin_id", disableFilters: true },

      {
        Header: "No of Active Activities",
        accessor: (row: any) => {
          const no_of_active_activities = row.active_activities?.length;
          return (
            <div className="flex items-center justify-center space-x-2">
              <span className="text-blue-600">{no_of_active_activities}</span>
              <Link 
                to={`/settings/config/activate-activity/${row.uin_id}`}
                className="text-blue-600 p-1.5 rounded-lg transition-all duration-200 hover:bg-blue-50 border border-transparent hover:border-blue-200 flex items-center justify-center"
                aria-label="View Activities"
              >
                <>{FaExternalLinkAlt({ size: 12 })}</>
              </Link>
            </div>
          );
        },
        disableFilters: true,
      },

      {
        Header: "No of Available Activities",
        accessor: (row: any) => {
          const no_of_available_activities = row.available_activities?.length;
          return no_of_available_activities;
        },
        disableFilters: true,
      },
      {
        Header: "Actions",
        id: "edit",
        disableFilters: true,
        Cell: ({ row }: any) => (
          <div className="flex items-center justify-center space-x-4 mx-auto">
            <Tooltip msg="Edit Configuration">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/settings/config/update-config/${row.original.uin_id}`, {
                    state: { data: row.original },
                  });
                }}
                className="text-blue-600 p-2.5 rounded-lg transition-all duration-200 hover:bg-blue-50 border border-transparent hover:border-blue-200 flex items-center justify-center"
                aria-label="Edit Configuration"
              >
                <>{FaEdit({ size: 16 })}</>
              </button>
            </Tooltip>

            <Tooltip msg="Update Zone">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/settings/config/update-zone/${row.original.uin_id}`, {
                    state: { data: row.original },
                  });
                }}
                className="text-green-600 p-2.5 rounded-lg transition-all duration-200 hover:bg-green-50 border border-transparent hover:border-green-200 flex items-center justify-center"
                aria-label="Update Zone"
              >
                <>{FaDrawPolygon({ size: 16 })}</>
              </button>
            </Tooltip>
            
            {/* <Tooltip msg="Update PTZ">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/settings/config/update-ptz/${row.original.uin_id}`, {
                    state: { data: row.original },
                  });
                }}
                className="text-purple-600 p-2.5 rounded-lg transition-all duration-200 hover:bg-purple-50 border border-transparent hover:border-purple-200 flex items-center justify-center"
                aria-label="Update PTZ"
              >
                <FaVideo size={16} />
              </button>
            </Tooltip> */}

            <Tooltip msg="Upload Config">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  uploadConfig(row.original.uin_id);
                }}
                className="text-orange-600 p-2.5 rounded-lg transition-all duration-200 hover:bg-orange-50 border border-transparent hover:border-orange-200 flex items-center justify-center"
                aria-label="Upload Config"
              >
                <>{FaUpload({ size: 16 })}</>
              </button>
            </Tooltip>
          </div>
        ),
      },
    ],
    [navigate]
  );

  const onValueChangeHandler = (value: string, name: string) => {
    setInput({
      ...input,
      [name]: value,
    });
  };


  const { mutate: configMutate } = useMutation(
    putRequest,
    optimisticOptions(queries.config)
  );

  const uploadConfig = (uin_id: string) => {
    configMutate({
      endPoint: `/config/upload/${uin_id}`,
      payload: {},
    });
    Toast("Uploading successfully");
  };



  return (
    <div className="bg-white rounded-xl shadow-md p-6 flex flex-col">
      <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row justify-between items-center border-b border-gray-200 pb-5 mb-5">
        <h1 className="text-2xl font-bold text-gray-800">Configuration Settings</h1>
        <div>
          <GlobalSearch
            value={input["search"]}
            setValue={(e: any) =>
              onValueChangeHandler(e.target.value, "search")
            }
            className="w-72 px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
          />
        </div>
      </div>

      
          <DataGrid
            data={config}
            columns={columns}
            onValueChangeHandler={onValueChangeHandler}
            filter={input.search}
            className="hover:bg-gray-50 transition-colors duration-200 text-center"
          />
      

      {totalPages > 1 && (
        <div className="flex justify-center items-center py-5 mt-auto">
          <ReactPaginate
            containerClassName="flex items-center space-x-3"
            pageClassName="flex items-center justify-center h-10 w-10 rounded-md border border-gray-300 transition-colors duration-200 cursor-pointer hover:bg-gray-100 text-gray-700"
            pageLinkClassName="flex items-center justify-center w-full h-full"
            activeClassName="!bg-blue-500 !text-white !border-blue-500 hover:!bg-blue-600"
            activeLinkClassName="text-white"
            onPageChange={onPageChangeHandler}
            pageCount={totalPages}
            breakLabel="..."
            previousLabel={
              <button 
                className="flex items-center justify-center h-10 w-10 rounded-md border border-gray-300 text-gray-700 transition-colors duration-200 hover:bg-gray-100 cursor-pointer"
                aria-label="Previous page"
              >
                <TriangleLeftIcon className="w-4 h-4" />
              </button>
            }
            nextLabel={
              <button 
                className="flex items-center justify-center h-10 w-10 rounded-md border border-gray-300 text-gray-700 transition-colors duration-200 hover:bg-gray-100 cursor-pointer"
                aria-label="Next page"
              >
                <TriangleRightIcon className="w-4 h-4" />
              </button>
            }
            forcePage={page - 1}
          />
        </div>
      )}
    </div>
  );
};
